import { FieldArray, FormikProps } from 'formik'
import { Typography } from '@mui/material'
import { TextInputV4 } from '@labrav/react-components'
import { getRandom, ShortenedNameV2 } from '@labrav/common'
import { get } from 'lodash'
import clsx from 'clsx'
import { useMemo } from 'react'
import { useFlyOutProductsBasedOnSelectedCloud } from '../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { Dimension } from '../../../modules/privateOffer/types'
import {
  ContainerPricingModel,
  Product,
} from '../../../modules/productsListing/types'
import { useStyles } from './ContractPricingTermV2.styles'

export interface ContractPricingTermV2Props {
  formik: FormikProps<Record<string, unknown>>
  productId: string
}

export const ContractPricingTermV2 = ({
  formik,
  productId,
}: ContractPricingTermV2Props) => {
  const { classes } = useStyles()
  const { values, errors, touched, handleChange, handleBlur } = formik
  const dimensions = (values.dimensions || []) as Dimension[]
  const dimensionsLength = dimensions?.filter(
    dimensionItem => dimensionItem.type === 'standard'
  ).length
  const randomKey = useMemo(() => getRandom(), [dimensions.length])
  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const product =
    products.find(product => product.productId === productId) || ({} as Product)

  const getIsError = (path: string) => {
    return !!(get(touched, path, false) && get(errors, path, false))
  }
  const getErrorText = (path: string) => {
    return (!!get(touched, path, false) && get(errors, path, '')) || ''
  }
  const getInputKey = (isFlexible: boolean) => {
    return isFlexible ? 'quantity' : 'price'
  }
  const flexiblePaymentTerms = useMemo(() => {
    return values?.flexiblePaymentTerms as boolean
  }, [values.flexiblePaymentTerms])

  const isContainerPricingModelHourly =
    product.containerPricingModel === ContainerPricingModel.HOURLY
  const isContainerPricingModelUsage =
    product.containerPricingModel === ContainerPricingModel.USAGE

  return (
    <>
      <div className={clsx(classes.root, classes.elementsGap)}>
        <div className={classes.titleWraper}>
          <Typography
            className={clsx(classes.sectionText, classes.columnContent)}
          >
            {isContainerPricingModelUsage ? 'Display name' : 'Name'}
          </Typography>
          {isContainerPricingModelHourly && flexiblePaymentTerms ? (
            <Typography
              className={clsx(classes.sectionText, classes.columnContent)}
            >
              Description
            </Typography>
          ) : (
            <></>
          )}

          <Typography
            className={clsx(classes.sectionText, classes.columnContent)}
          >
            {isContainerPricingModelHourly && flexiblePaymentTerms
              ? 'Quantity'
              : 'Price($)'}
          </Typography>
        </div>
        <div className={classes.border}></div>
        <FieldArray name="dimensions">
          {() => (
            <>
              {dimensions.map((dimension, index) =>
                dimension.type === 'standard' ? (
                  <>
                    <div
                      className={classes.resourceRow}
                      data-testid="dimensions-row"
                    >
                      <div className={classes.resourcesContainer}>
                        <div
                          className={clsx(
                            classes.alignDescription,
                            classes.columnItems
                          )}
                          key={`dimensions-${randomKey}-label`}
                          data-testid="description-label"
                        >
                          {dimensions[index].label}
                        </div>
                        {isContainerPricingModelHourly &&
                        flexiblePaymentTerms ? (
                          <div
                            className={clsx(
                              classes.alignDescription,
                              classes.columnItems
                            )}
                            key={`dimensions-${randomKey}-desc`}
                            data-testid="description-labelDescription"
                          >
                            <ShortenedNameV2
                              name={dimensions[index].labelDescription || '-'}
                              length={15}
                            />
                          </div>
                        ) : (
                          <></>
                        )}
                        <div
                          key={`dimensions-${randomKey}-${getInputKey(
                            flexiblePaymentTerms
                          )}`}
                          className={clsx(
                            classes.alignDescription,
                            classes.columnItems
                          )}
                        >
                          <TextInputV4
                            keyName={`dimensions.${[index]}.${getInputKey(
                              flexiblePaymentTerms
                            )}`}
                            title={flexiblePaymentTerms ? 'Quantity' : 'Price'}
                            type="number"
                            placeHolder=""
                            value={
                              flexiblePaymentTerms
                                ? (dimensions[index]?.quantity as number)
                                : (dimensions[index]?.price as number)
                            }
                            isError={getIsError(
                              `dimensions.${[index]}.${getInputKey(
                                flexiblePaymentTerms
                              )}`
                            )}
                            errorText={getErrorText(
                              `dimensions.${[index]}.${getInputKey(
                                flexiblePaymentTerms
                              )}`
                            )}
                            onChange={handleChange}
                            isReadOnly={false}
                            isRequired={false}
                            showSpinner={false}
                            onBlur={handleBlur}
                            keyToPrevent={['+', '-', 'e']}
                          />
                        </div>
                      </div>
                    </div>
                    {dimensionsLength !== index + 1 ? (
                      <div className={classes.border}></div>
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  <></>
                )
              )}
            </>
          )}
        </FieldArray>
      </div>
    </>
  )
}
